import { Suspense, useEffect } from 'react';

import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { SkipToContent } from '@/components/accessibility/SkipToContent';
import { PageSkeleton } from '@gw/ui';
import { CookieConsent } from '@/components/cookie-consent';
import { AppLayout } from '@/components/layouts/AppLayout';
import { CenteredContent } from '@/components/layouts/CenteredContent';
import { TokenPurchaseModal } from '@/components/modals/TokenPurchaseModal';
import { ProtectedRoute } from '@/components/routes/ProtectedRoute';
import { RouteErrorBoundary } from '@/components/routes/RouteErrorBoundary';
import { TOSGuard } from '@/components/routes/TOSGuard';
import { Toaster } from '@/components/ui/sonner';
import { UpdateBanner } from '@/components/UpdateBanner';
import { useAppFreshness } from '@/hooks/useAppFreshness';
import { useAuthErrorHandler } from '@/hooks/useAuthErrorHandler';
import { useNotificationsConnection } from '@/hooks/useNotificationsConnection';
import { lazyWithRetry } from '@/lib/lazyWithRetry.util';
import { resolveRouterBasename } from '@/lib/version-bootstrap';
import { authRoutes } from '@/routes/auth.routes';
import { consultantRoutes } from '@/routes/consultant.routes';
import { marketplaceProtectedRoutes, marketplaceRoutes } from '@/routes/marketplace.routes';
import { organizationRoutes } from '@/routes/organization.routes';
import { personalRoutes } from '@/routes/personal.routes';
import { publicRoutes } from '@/routes/public.routes';
import { publisherRoutes } from '@/routes/publisher.routes';
import { settingsRoutes } from '@/routes/settings.routes';
import { useAuthStore, useFeatureFlagsStore, useOrganizationStore } from '@/stores/store';

// ============================================================================
// App: providers, router scaffold, and composition of the by-domain route
// fragments in src/routes/*.routes.tsx. Only routes that belong to the
// scaffold itself (the two catch-alls and /osint-missions, which sits under
// ProtectedRoute but outside TOSGuard/AppLayout) are declared inline here.
// Route fragments are React fragments of <Route> elements — react-router v6
// flattens fragments in <Routes>, and sibling matching is rank-based, so
// fragment order below is organizational, not behavioral.
// ============================================================================

// GW-10439/GW-10551: single consolidated 404 page, rendered through the
// `default` AppLayout shell for BOTH auth states, centered via a nested
// CenteredContent wrapper.
const NotFoundPage = lazyWithRetry(() =>
  import('@/pages/public/NotFoundPage').then((m) => ({ default: m.NotFoundPage }))
);

// GW-10532/GW-10551: single catch-all (404) at the top level, OUTSIDE
// ProtectedRoute — a protected catch-all would send anonymous visitors through
// the auth guard to /auth/login instead of the consolidated 404. The two former
// `path="*"` routes (one nested in ProtectedRoute, one bare) shared the same
// react-router rank, so the earlier-declared protected one won for EVERY
// unmatched URL and shadowed the 404 for logged-out users.
//
// GW-10551 de-auths this component: it renders unconditionally through the
// `default` authed shell, so the sidebar is present iff authenticated (the
// "sidebar iff authenticated" principle is handled entirely by the shell — no
// `isAuthenticated` branching here). CenteredContent nests INSIDE the shell so
// the 404 is centered regardless of auth. This removes the last chrome-by-auth
// branching site in the app.
function NotFoundCatchAll() {
  return (
    <AppLayout variant="default">
      <CenteredContent>
        <Suspense fallback={<PageSkeleton />}>
          <NotFoundPage />
        </Suspense>
      </CenteredContent>
    </AppLayout>
  );
}
// GW-9283: OSINT Mission Gallery
const OsintMissionsPage = lazyWithRetry(() =>
  import('@/pages/OsintMissionsPage').then((m) => ({ default: m.OsintMissionsPage }))
);

function App() {
  const { checkAuth, isAuthenticated, hasLoaded: authHasLoaded } = useAuthStore();
  const { currentOrg } = useOrganizationStore();
  const { fetchAllKnownFlags } = useFeatureFlagsStore();
  const { showBanner, dismissBanner } = useAppFreshness();

  // GW-2099: Handle stale Cognito session token expiry
  useAuthErrorHandler();

  // GW-8887: Open the single app-wide notifications AppSync Events socket
  // after auth (flag-gated; inert when the transport is unconfigured).
  useNotificationsConnection();

  useEffect(() => {
    // Check authentication status on app mount ONCE
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run only on mount, checkAuth is stable from Zustand
  }, []);

  // Fetch all known flags once we know how to fetch them safely:
  // - Authenticated: wait for org context too — the authenticated feature-flag
  //   endpoint requires X-Organization-Id, and firing before currentOrg is set
  //   sends a headerless request that returns 401 and triggers a spurious
  //   session-expired logout (GW-6672).
  // - Unauthenticated: fetchAllKnownFlags already calls the public endpoint
  //   for this case (GW-2078) — no org context is needed or possible, so fire
  //   as soon as checkAuth has resolved confirming logged-out status. Without
  //   this, public flag-gated routes (e.g. /marketplace/missions/:slug) hang
  //   on FeatureFlagGuard's loading skeleton forever for signed-out visitors,
  //   since `isAuthenticated && currentOrg?.id` never becomes true for them.
  useEffect(() => {
    if (isAuthenticated && currentOrg?.id) {
      fetchAllKnownFlags();
    } else if (!isAuthenticated && authHasLoaded.checkAuth) {
      fetchAllKnownFlags();
    }
  }, [isAuthenticated, currentOrg?.id, authHasLoaded.checkAuth, fetchAllKnownFlags]);

  return (
    <HelmetProvider>
      <SkipToContent />
      <CookieConsent />
      <Toaster position="top-right" closeButton richColors />
      <UpdateBanner
        showBanner={showBanner}
        onDismiss={dismissBanner}
        onRefresh={() => window.location.reload()}
      />
      <BrowserRouter
        basename={resolveRouterBasename()}
        future={{ v7_relativeSplatPath: true, v7_startTransition: true }}
      >
        {/* GW-6716: Global token purchase modal — mounted once at router root */}
        <TokenPurchaseModal />
        <Routes>
          {/* GW-8180: Wrap all routes in RouteErrorBoundary for chunk-load recovery */}
          <Route element={<RouteErrorBoundary />}>
            {/* Public/optional-auth surface */}
            {marketplaceRoutes}
            {publicRoutes}
            {authRoutes}

            {/* Auth-only, no org selection required (GW-452) */}
            {settingsRoutes}

            {/* Protected routes (require authentication AND org selection) */}
            <Route element={<ProtectedRoute />}>
              {/* GW-9283/GW-10440: OSINT Mission Gallery — auth required, renders
                  through the unified AppLayout shell's DEFAULT authed variant with
                  hideSidebar (Header + footer chrome, no Sidebar). Stays outside
                  TOSGuard, as it is today. */}
              <Route element={<AppLayout variant="default" hideSidebar />}>
                <Route
                  path="/osint-missions"
                  element={
                    <Suspense fallback={<PageSkeleton />}>
                      <OsintMissionsPage />
                    </Suspense>
                  }
                />
              </Route>

              {/* Routes protected by TOS acceptance */}
              <Route element={<TOSGuard />}>
                {/* GW-10434: protected app route group renders through the
                    unified AppLayout shell's DEFAULT authed variant (Sidebar +
                    Header + ProfileCompletenessBanner + SessionCompanionBar via
                    LayoutShell). The `default` variant is explicit here so the
                    consolidation intent is legible alongside the sibling slices
                    that pass `bare`/`centered`. */}
                <Route element={<AppLayout variant="default" />}>
                  {marketplaceProtectedRoutes}
                  {publisherRoutes}
                  {organizationRoutes}
                  {personalRoutes}
                  {consultantRoutes}
                </Route>
              </Route>
            </Route>

            {/* Catch all - 404 Not Found page
                (GW-503/GW-1993/GW-10439/GW-10532/GW-10551). A SINGLE catch-all
                at the top level (outside ProtectedRoute) so it is reachable for
                BOTH auth states without a redirect to /auth/login. GW-10551
                renders it identically for both: the `default` AppLayout shell
                (sidebar present iff authenticated) with a nested CenteredContent
                wrapper — there is no separate logged-out variant. See
                NotFoundCatchAll above for why this must not be nested under
                ProtectedRoute. */}
            <Route path="*" element={<NotFoundCatchAll />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
